import PropTypes from 'prop-types';
import styles from './FJGenerateButton.module.less';
import cionNew from '../../../assets/base64/cion-new.js';
import FJLocalStore from '../../../store/FJLocalStore';
import classNames from 'classnames';
import FJLoading from '../FJLoading/FJLoading';
import FJEllipsisLoading from '../FJEllipsisLoading/FJEllipsisLoading';
import FJConstant from '../../../constants/FJConstant';
import { useMemo, useRef, forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import FJUtil from '../../../util/FJUtil';
import FJUsageDetailTwoContainer from '../../combineComponents/FJUsageDetail/container/FJUsageDetailContainerTwo';
import FJConfig from '../../../config/FJConfig';
import FJAppConstant from '../../../constants/FJAppConstant';

const FJGenerateButton = forwardRef((props, ref) => {
    const {
        id,
        className,
        onClick,
        isFree,
        useCredit,
        taskStatus,
        isProjectLoading,
        freeNumCreation,
        text,
        style,
        disabled = false,
        isShowUsageDetail = false,
        isHideCredit = false,
        hideUseCredit = false,
        hideFreeCreation = false,
        useCreditClassName,
        usageDetailOffsetLeft,
        usageDetailOffsetTop,
        usageDetailPlacement,
        isFreeBottom = false,
        /**
         * 默认免费次数
         * 未登录状态，AI工具若有默认免费使用次数，生成按钮/上传区域显示当前工具的默认免费试用次数。没有免费试用次数的AI工具隐藏生成按钮/上传区域的价格/扣费规则相关文本。
         * 为 0 时不显示
         */
        defaultFreeNumCreation = 0,
    } = props;

    const btnText = useMemo(() => {
        if (text) {
            return text;
        }
        if (taskStatus === FJConstant.START) {
            return 'start';
        } else if (taskStatus === FJConstant.WAITING) {
            return 'queuing';
        } else if (taskStatus === FJConstant.GENERATING) {
            return FJConfig.appType === FJAppConstant.VIDEO_REMOVE_BG ? 'removing' : 'generating';
        } else if (isFree) {
            return 'generate';
            // return 'free-creation';
        } else {
            return 'generate';
        }
    }, [taskStatus, isFree, text]);

    const isGenerating = FJUtil.isGeneratingStatus(taskStatus);
    const isLogin = FJUtil.isLogin();
    const isHideUseCredit = isProjectLoading || isFree;
    const creditText = useCredit > 0 ? `-${useCredit}` : 0;

    const usageDetailRef = useRef(null);
    const usageDetailTriggerRef = useRef(null);
    const btnRef = useRef(null);

    const [offsetLeft, setOffsetLeft] = useState(usageDetailOffsetLeft);
    const [offsetTop, setOffsetTop] = useState(usageDetailOffsetTop);

    const _renderTrigger = config => {
        return (
            <div
                className={classNames(styles.use_credit, useCreditClassName)}
                onMouseMove={config?.onMouseMove}
                ref={target => {
                    usageDetailTriggerRef.current = target;
                    config?.ref && (config.ref.current = target);
                }}
            >
                {/* <CreditIcon className={styles.credit_icon} /> */}
                <img src={cionNew} alt='credit' className={styles.credit_icon} />
                <span className={styles.use_credit_num}>{creditText}</span>
            </div>
        );
    };

    const _onClick = () => {
        usageDetailRef.current?.hide();
        onClick();
    };

    useImperativeHandle(ref, () => ({
        hideUsageDetail: () => {
            usageDetailRef.current?.hide();
        },
    }));
    const isShowFreeCreation = isLogin
        ? !isHideCredit && isFree && !hideFreeCreation && !isFreeBottom
        : !isHideCredit && !hideFreeCreation && !isFreeBottom && defaultFreeNumCreation > 0;
    const isShowUseCredit = !isHideCredit && !isHideUseCredit && isLogin && !hideUseCredit;

    /**
     * 自动计算使用详情弹窗的位置偏移量
     * 当没有手动指定偏移量和位置时，自动计算按钮与触发元素之间的相对位置
     */
    useEffect(() => {
        // 检查是否没有手动指定偏移量和位置参数
        if (
            usageDetailOffsetLeft === undefined &&
            usageDetailOffsetTop === undefined &&
            usageDetailPlacement === undefined
        ) {
            if (isShowUseCredit && isShowUsageDetail) {
                const threshold = 10; // 弹窗与按钮之间的最小间距（像素）

                // 获取按钮和触发元素的位置信息
                const btnRect = btnRef.current.getBoundingClientRect();
                const triggerRect = usageDetailTriggerRef.current.getBoundingClientRect();

                // 计算水平偏移量：按钮中心点 - 触发元素中心点
                setOffsetLeft(btnRect.left + btnRect.width / 2 - (triggerRect.left + triggerRect.width / 2));
                // 计算垂直偏移量：按钮顶部 - 阈值 - 触发元素中心点
                setOffsetTop(btnRect.top - threshold - (triggerRect.top + triggerRect.height / 2));
            }
        }
    }, [isShowUseCredit, isShowUsageDetail, usageDetailOffsetLeft, usageDetailOffsetTop, usageDetailPlacement]);

    const freeNumber = freeNumCreation;

    return (
        <>
            <div
                className={classNames(
                    styles.btn_box,
                    isGenerating ? styles.generating : '',
                    disabled ? styles.disabled : '',
                    className,
                )}
                onClick={_onClick}
                style={style}
                id={id}
                ref={btnRef}
            >
                <div className={styles.text_box}>
                    {isProjectLoading ? (
                        <FJLoading />
                    ) : (
                        <>
                            <span className={styles.btn_text}>{FJLocalStore._(btnText)}</span>
                            {isGenerating && <FJEllipsisLoading />}
                            {isShowFreeCreation && (
                                <span className={styles.free_creation}>
                                    {!isLogin
                                        ? FJLocalStore._('free-try')
                                        : freeNumber === 1
                                          ? FJLocalStore._('free-trie-left')
                                          : FJLocalStore._('free-tries-left').replace('xxx', freeNumber)}
                                </span>
                            )}

                            {isShowUseCredit &&
                                (isShowUsageDetail ? (
                                    <FJUsageDetailTwoContainer
                                        ref={usageDetailRef}
                                        renderTrigger={_renderTrigger}
                                        offsetLeft={offsetLeft}
                                        offsetTop={offsetTop}
                                        placement={usageDetailPlacement}
                                    />
                                ) : (
                                    _renderTrigger()
                                ))}
                        </>
                    )}
                    <div className={styles.background_hover}></div>
                </div>
            </div>
            {isFreeBottom && !isLogin && defaultFreeNumCreation > 0 && (
                <div
                    className={styles.free_creation_bottom}
                    dangerouslySetInnerHTML={{
                        __html: FJLocalStore._('free-try'),
                    }}
                />
            )}

            {isFreeBottom && isFree && isLogin && (
                <div
                    className={styles.free_creation_bottom}
                    dangerouslySetInnerHTML={{
                        __html:
                            freeNumber === 1
                                ? FJLocalStore._('free-trie-left')
                                : FJLocalStore._('free-tries-left').replace('xxx', freeNumber),
                    }}
                />
            )}
        </>
    );
});

FJGenerateButton.displayName = 'FJGenerateButton';
FJGenerateButton.propTypes = {
    onClick: PropTypes.func,
    isLoading: PropTypes.bool,
    useCredit: PropTypes.number,
    isFree: PropTypes.bool,
    className: PropTypes.string,
    taskStatus: PropTypes.string,
    isProjectLoading: PropTypes.bool,
    freeNumCreation: PropTypes.number,
    disabled: PropTypes.bool,
    useCreditClassName: PropTypes.string,
};
export default FJGenerateButton;
